import { useCallback, useEffect, useMemo, useState } from "react";
import type { Row, TabDef } from "../types";
import { api, ApiError } from "../api";
import { formatCell, statusTone } from "../format";
import { ordenarPorNome } from "../ordem";
import RecordForm from "./RecordForm";
import ErrorState from "./ErrorState";
import { IconSearch, IconPlus, IconEdit, IconTrash } from "../icons";
import { usePodeExcluir } from "../permissoes";

interface Props {
  tab: TabDef;
  itens: Row[];
  onDataChanged: () => void; // avisa o App para recarregar itens/dashboard
}

export default function CrudView({ tab, itens, onDataChanged }: Props) {
  const podeExcluir = usePodeExcluir(); // só admin vê a lixeira
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState<Row | null>(null);
  const [formOpen, setFormOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(await api.list(tab.resource));
    } catch (err) {
      setError(err instanceof ApiError ? err : new ApiError(String(err)));
    } finally {
      setLoading(false);
    }
  }, [tab.resource]);

  useEffect(() => {
    setSearch("");
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const achados = !q
      ? rows
      : rows.filter((r) =>
          tab.fields.some((f) => {
            const value = f.ref === "itens" ? r.item_descricao : r[f.name];
            return String(value ?? "").toLowerCase().includes(q);
          }),
        );
    // Aba de cadastro por nome (Itens) abre em ordem alfabética; Movimentações
    // e Inventários seguem a ordem da API (mais recente primeiro).
    const campo = tab.ordenarPor;
    return campo ? ordenarPorNome(achados, (r) => r[campo]) : achados;
  }, [rows, search, tab.fields, tab.ordenarPor]);

  function openNew() {
    setEditing(null);
    setFormOpen(true);
  }

  function openEdit(row: Row) {
    setEditing(row);
    setFormOpen(true);
  }

  async function handleSubmit(data: Row) {
    if (editing) {
      await api.update(tab.resource, editing.id, data);
    } else {
      await api.create(tab.resource, data);
    }
    setFormOpen(false);
    await load();
    onDataChanged();
  }

  async function handleDelete(row: Row) {
    if (!confirm("Tem certeza que deseja excluir este registro?")) return;
    try {
      await api.remove(tab.resource, row.id);
      await load();
      onDataChanged();
    } catch (err) {
      alert(err instanceof Error ? err.message : "Erro ao excluir");
    }
  }

  if (error)
    return <ErrorState message={error.message} code={error.code} onRetry={load} />;

  return (
    <div className="crud">
      <div className="crud-toolbar">
        <div className="search-wrap">
          <IconSearch size={17} />
          <input
            className="search"
            placeholder="Buscar..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="toolbar-right">
          <span className="badge">{filtered.length} registro(s)</span>
          <button className="btn-primary" onClick={openNew}>
            <IconPlus size={17} /> Novo
          </button>
        </div>
      </div>

      <div className="table-wrap">
        {loading ? (
          <div className="empty">Carregando...</div>
        ) : filtered.length === 0 ? (
          <div className="empty">Nenhum registro encontrado.</div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                {tab.fields.filter((f) => !f.hideInTable).map((f) => (
                  <th key={f.name}>{f.label}</th>
                ))}
                <th className="col-actions">Ações</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((row) => (
                <tr key={row.id}>
                  {tab.fields.filter((f) => !f.hideInTable).map((f) => {
                    const text = formatCell(f, row);
                    return (
                      <td key={f.name}>
                        {f.type === "status" && text !== "—" ? (
                          <span className={`pill pill-${statusTone(text)}`}>{text}</span>
                        ) : (
                          text
                        )}
                      </td>
                    );
                  })}
                  <td className="col-actions">
                    <button className="icon-btn" title="Editar" onClick={() => openEdit(row)}>
                      <IconEdit size={17} />
                    </button>
                    {podeExcluir && (
                      <button
                        className="icon-btn icon-btn-danger"
                        title="Excluir"
                        onClick={() => handleDelete(row)}
                      >
                        <IconTrash size={17} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {formOpen && (
        <RecordForm
          title={editing ? `Editar — ${tab.label}` : `Novo — ${tab.label}`}
          fields={tab.fields}
          initial={editing}
          itens={itens}
          onCancel={() => setFormOpen(false)}
          onSubmit={handleSubmit}
        />
      )}
    </div>
  );
}
